import {
    Box,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Chip,
    useTheme,
    alpha
} from '@mui/material';
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import moment from 'moment';

interface LicenseStatusCardProps {
    licenseStatus: string;
    inicio_licence: Date | null,
    final_licence: Date | null,
    chave_licence?: string,
    nome_plano_licence?: string
}

const LicenseStatusCard = ({ licenseStatus, inicio_licence, final_licence, chave_licence, nome_plano_licence }: LicenseStatusCardProps) => {
    const theme = useTheme();

    // Determine status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return theme.palette.success.main;
            case 'expiring':
                return theme.palette.warning.main;
            case 'expired':
                return theme.palette.error.main;
            default:
                return theme.palette.primary.main;
        }
    };

    // Get status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
                return <CheckCircle size={20} color={theme.palette.success.main} />;
            case 'expiring':
                return <AlertCircle size={20} color={theme.palette.warning.main} />;
            case 'expired':
                return <XCircle size={20} color={theme.palette.error.main} />;
            default:
                return <XCircle size={20} color={theme.palette.error.main} />;
        }
    };

    // Calculate percentage of days remaining
    const periodoLicence = final_licence ? moment(final_licence).diff(inicio_licence, 'days') : 30;
    const difLicence = inicio_licence ? moment().diff(inicio_licence, 'days') : 1;
    const daysRemainingPercentage = (Number(difLicence) / Number(periodoLicence)) * 100;

    // Status message
    const getStatusMessage = (status: string) => {
        switch (status) {
            case 'active':
                return 'Licença ativa';
            case 'expiring':
                return 'A licença expira em breve';
            case 'expired':
                return 'A licença expirou';
            default:
                return '';
        }
    };

    const statusColor = getStatusColor(licenseStatus);
    const progressColor = licenseStatus === 'expired' ? theme.palette.error.main :
        licenseStatus === 'expiring' ? theme.palette.secondary.main :
            theme.palette.success.main;

    return (
        <Card sx={{
            height: '100%',
            borderLeft: `4px solid ${statusColor}`,
            transition: 'transform 0.2s',
            '&:hover': {
                transform: 'translateY(-2px)'
            }
        }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" component="div" gutterBottom>
                        Status da Licença
                    </Typography>
                    <Chip
                        icon={getStatusIcon(licenseStatus)}
                        label={getStatusMessage(licenseStatus)}
                        sx={{
                            backgroundColor: alpha(statusColor, 0.1),
                            color: statusColor,
                            fontWeight: 500,
                            '& .MuiChip-icon': {
                                color: statusColor
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Dias de Uso
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {difLicence} / {periodoLicence}
                        </Typography>
                    </Box>

                    <LinearProgress
                        variant="determinate"
                        value={daysRemainingPercentage}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: alpha(progressColor, 0.2),
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: progressColor
                            }
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Chave da Licença
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                            {chave_licence || ""}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">
                            Dados da Licença
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                            {nome_plano_licence || ""}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default LicenseStatusCard;